import React from "react";
import { Facebook } from "lucide-react";

const theProductImg = "/images/the-product.jpg";

export function Contact() {
  const [formData, setFormData] = React.useState({
    name: "",
    message: "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Customer Message : ${formData.name}`;
    const body = formData.message;

    window.open(
      `mailto:hello@sansifoods.lk?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    );
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      {/* Left Column: Full-height Image with Testimonial */}
      <div className="relative hidden lg:block min-h-screen">
        {/* Full-bleed background image */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${theProductImg})`,
          }}
        >
          {/* Gradient overlay for better text readability */}
          <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/30 to-transparent" />

          {/* Blue tint overlay similar to reference */}
          <div className="absolute inset-0 bg-linear-to-br from-[#0a1628]/60 to-transparent mix-blend-multiply" />
        </div>
      </div>

      {/* Right Column: Content & Form */}
      <div className="relative min-h-screen bg-sansi-black flex flex-col justify-center px-6 py-16 lg:px-12 xl:px-20 pt-32">
        <div className="space-y-8 animate-in slide-in-from-left duration-700 fade-in max-w-lg">
          <div className="space-y-4">
            <h1 className="font-display text-5xl md:text-8xl uppercase text-white leading-none tracking-tight">
              Contact
            </h1>
            <p className="text-xl text-gray-400 font-light max-w-lg italic">
              Have a question, a comment, a very good idea or a complaint? We'd
              love to hear from you! Reach out to us via this form.
            </p>
          </div>

          <div className="font-display text-lg sm:text-2xl md:text-3xl text-sansi-orange uppercase tracking-wider">
            hello@sansifoods.lk
          </div>

          {/* Address Section */}
          <div className="space-y-2 text-gray-300 font-light">
            {/* <div className="font-display text-lg sm:text-xl text-white uppercase tracking-wider">
              Address
            </div> */}
            <div className="text-base sm:text-lg leading-relaxed">
              <div className="font-semibold text-white">Sansi Eco Foods</div>
              <div>No 364, Welewawa, Waliagaraya</div>
              <div>Nawagaththegama</div>
              <div>Puttalam</div>
            </div>
          </div>

          {/* WhatsApp Button - Home Page Style */}
          <a
            href="https://wa.me/94740701231?text=Hello!%20I%20would%20like%20to%20know%20more%20about%20Sansi%20Foods%20products."
            target="_blank"
            rel="noopener noreferrer"
            className="group relative inline-flex items-center justify-center gap-3 px-8 py-4 bg-sansi-green text-black font-black uppercase tracking-wider text-lg hover:bg-white transition-colors duration-300 transform hover:-translate-y-1 hover:shadow-[4px_4px_0px_var(--color-sansi-orange)] rounded-none"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-6 h-6"
            >
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span className="font-display text-lg sm:text-xl">
              +94 (74) 070 1231
            </span>
          </a>

          <div className="flex gap-6">
            <a
              href="https://www.facebook.com/share/19ZW9DJAd2/?mibextid=wwXIfr"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-sansi-orange transition-colors hover:scale-110 transform duration-300"
            >
              <Facebook size={32} />
            </a>
            <a
              href="https://www.tiktok.com/@sansiecofood"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-sansi-orange transition-colors hover:scale-110 transform duration-300"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="lucide lucide-tiktok"
              >
                <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
              </svg>
            </a>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6 max-w-lg">
            <div>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                type="text"
                placeholder="Name *"
                className="w-full bg-white/10 hover:bg-white/15 focus:bg-white/20 transition-colors border-none p-4 text-white text-lg placeholder-gray-500 outline-none focus:ring-2 focus:ring-sansi-green rounded-none"
                required
              />
            </div>

            <div>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Message *"
                rows={5}
                className="w-full bg-white/10 hover:bg-white/15 focus:bg-white/20 transition-colors border-none p-4 text-white text-lg placeholder-gray-500 outline-none focus:ring-2 focus:ring-sansi-green resize-none"
                required
              />
            </div>

            <button
              type="submit"
              className="bg-sansi-orange text-black font-black uppercase text-xl px-10 py-4 hover:bg-white hover:scale-105 transition-all duration-300 shadow-[4px_4px_0px_rgba(0,0,0,0.5)] hover:shadow-[6px_6px_0px_var(--color-sansi-green)] transform rounded-none"
            >
              Send to Sansi
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
